import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Info,
  X,
  Layers,
  User,
} from "lucide-react";

import styles from "./Odontograma.module.scss";
import { useLoading } from "../../../../../shared/context/LoadingContext";

/* Estado de um procedimento sozinho. É o que aparece na etiqueta de cada
   procedimento, na lista do dente. Um isolado nunca está «em execução»: tem
   uma sessão só — ou está feito, ou não. */
const estadoDoProcedimento = (proc) => {
  if (proc.procedimento_completo) return "executado";
  if (!proc.isIsolado && proc.data_inicio_execucao && !proc.data_fim_execucao)
    return "em_execucao";
  return "por_executar";
};

/**
 * A cor de um dente (ou de uma arcada) — o ponto em que está o TRABALHO nele,
 * e não só o do procedimento mais ativo.
 *
 *   laranja (por executar) — há trabalho planeado e ninguém lhe tocou;
 *   ciano   (em execução)  — o trabalho começou e ainda falta;
 *   verde   (executado)    — está tudo feito.
 *
 * A regra de 2026-09-17 é o caso «algo feito + algo por fazer». Antes ficava
 * laranja, o que se lia como «ninguém tocou neste dente» quando já lá havia
 * uma restauração feita. Agora fica em execução — mas SÓ se o que foi feito
 * for posterior a ter sido planeado o que está por fazer, ou seja, se alguém
 * já começou esse trabalho. Uma restauração de 2021 ao lado de uma coroa
 * planeada hoje é outro episódio, acabado há anos: o dente fica laranja.
 *
 * Compara com o planeamento MAIS ANTIGO entre os pendentes: basta alguma coisa
 * ter sido feita depois de a mais antiga das pendentes existir.
 */
const estadoDoDente = (procedimentos) => {
  if (!procedimentos || procedimentos.length === 0) return null;

  const estados = procedimentos.map(estadoDoProcedimento);

  if (estados.includes("em_execucao")) return "em_execucao";

  if (estados.includes("por_executar")) {
    const planeamentos = procedimentos
      .filter((p, i) => estados[i] === "por_executar" && p.data_planeamento)
      .map((p) => new Date(p.data_planeamento).getTime());

    const planeadoPrimeiro =
      planeamentos.length > 0 ? Math.min(...planeamentos) : null;

    const comecou =
      planeadoPrimeiro !== null &&
      procedimentos.some(
        (p, i) =>
          estados[i] === "executado" &&
          p.data_execucao &&
          new Date(p.data_execucao).getTime() > planeadoPrimeiro,
      );

    return comecou ? "em_execucao" : "por_executar";
  }

  if (estados.includes("executado")) return "executado";

  return null;
};

const Odontograma = ({ clienteId, sendRequest, auth }) => {
  const [planosTratamento, setPlanosTratamento] = useState([]);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [hoveredTooth, setHoveredTooth] = useState(null);
  const [selectedArcada, setSelectedArcada] = useState(null);
  const [arcadaTratamentos, setArcadaTratamentos] = useState({
    superior: [],
    inferior: [],
    ambas: [],
  });
  const { startLoading, stopLoading } = useLoading();

  // Verificar se é tratamento de arcada
  const isArcadaTratamento = (proc) => {
    return (
      !proc.dente ||
      proc.dente === "Arcadas" ||
      proc.dente.toLowerCase().includes("arcada")
    );
  };

  // Data de início de um procedimento: usa a data própria do procedimento
  // (definida ao finalizar) ou, em falta, a data da 1ª sessão registada — para
  // que um procedimento com sessões em curso conte como "em execução".
  const calcularDataInicioProcedimento = (pr) => {
    if (pr.data_inicio_execucao) return new Date(pr.data_inicio_execucao);

    if (Array.isArray(pr.sessoes) && pr.sessoes.length > 0) {
      const datas = pr.sessoes
        .map((s) => s.data_inicio_execucao)
        .filter(Boolean)
        .map((d) => new Date(d));
      if (datas.length > 0) return new Date(Math.min(...datas));
    }

    return null;
  };

  // Simular carregamento de dados com mais procedimentos
  useEffect(() => {
    const fetchData = async () => {
      try {
        startLoading();
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/odontograma/cliente/${clienteId}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        const procedimentos = [];
        const arcadaProcs = {
          superior: [],
          inferior: [],
          ambas: [],
        };

        responseData.planosTratamento.forEach((pl) => {
          // Verificar se é o "plano virtual" de tratamentos isolados
          const isPlanoIsolados = pl.id === "tratamentos-isolados";

          pl.procedimentos.forEach((pr) => {
            const procData = {
              dente: pr.dente,
              tratamento: { designacao: pr.tratamento.designacao },
              // Data de início = data do procedimento ou, se ainda não estiver
              // preenchida (só é definida ao finalizar), a data da 1ª sessão.
              // Assim um procedimento com sessões já conta como "em execução".
              data_inicio_execucao: calcularDataInicioProcedimento(pr),
              data_fim_execucao:
                pr.procedimento_completo && pr.data_fim_execucao
                  ? new Date(pr.data_fim_execucao)
                  : null,
              procedimento_completo: pr.procedimento_completo,
              // Para a cor do dente — ver `estadoDoDente`.
              data_planeamento: pr.data_planeamento
                ? new Date(pr.data_planeamento)
                : null,
              data_execucao: pr.data_execucao ? new Date(pr.data_execucao) : null,
              tipo: pr.tipo || "plano", // "isolado" ou "plano"
              planoId: pl.id,
              isIsolado: isPlanoIsolados,
              medico: pr.medico,
              observacoes: pr.observacoes,
              relatorio: pr.relatorio,
              _id: pr._id,
            };

            // Separar tratamentos de arcada
            if (isArcadaTratamento(pr)) {
              const denteText = pr.dente?.toLowerCase() || "";
              if (denteText.includes("superior")) {
                arcadaProcs.superior.push(procData);
              } else if (denteText.includes("inferior")) {
                arcadaProcs.inferior.push(procData);
              } else {
                arcadaProcs.ambas.push(procData);
              }
            } else {
              procedimentos.push(procData);
            }
          });
        });

        setPlanosTratamento([{ id: "1", procedimentos: procedimentos }]);
        setArcadaTratamentos(arcadaProcs);
      } catch (err) {
        console.error(err);
      } finally {
        stopLoading();
      }
    };
    fetchData();
  }, [clienteId]);

  const getToothStatus = (toothNumber) => {
    const procedimentos = planosTratamento.flatMap((plano) =>
      plano.procedimentos.filter(
        (proc) => proc.dente === toothNumber.toString()
      )
    );

    return estadoDoDente(procedimentos);
  };

  const getToothProcedures = (toothNumber) => {
    return planosTratamento
      .flatMap((plano) =>
        plano.procedimentos.filter(
          (proc) => proc.dente === toothNumber.toString()
        )
      )
      .map((proc) => ({
        ...proc,
        status: proc.procedimento_completo
          ? "executado"
          : !proc.isIsolado &&
            proc.data_inicio_execucao &&
            !proc.data_fim_execucao
          ? "em_execucao"
          : "por_executar",
      }));
  };

  const getArcadaStatus = (arcada) => {
    const tratamentos = [
      ...arcadaTratamentos[arcada],
      ...(arcada !== "ambas" ? arcadaTratamentos.ambas : []),
    ];

    return estadoDoDente(tratamentos);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "executado":
        return "#12d453";
      case "em_execucao":
        return "#11cce6";
      case "por_executar":
        return "#fdcb7f";
      default:
        return "#ffffff";
    }
  };

  const formatDate = (date) => {
    if (!date) return "Não agendado";
    return new Date(date).toLocaleDateString("pt-PT");
  };

  const ToothPath = ({ d, toothNumber, transform = "" }) => {
    const status = getToothStatus(toothNumber);
    const isHovered = hoveredTooth === toothNumber;
    const isSelected = selectedTooth === toothNumber;
    const procedures = getToothProcedures(toothNumber);
    const isLowerJaw =
      (toothNumber >= 31 && toothNumber <= 48) ||
      (toothNumber >= 71 && toothNumber <= 85);

    return (
      <g transform={transform}>
        {/* Sombra do dente */}
        <path
          d={d}
          fill="#000"
          opacity="0.15"
          transform="translate(2, 2)"
          style={{ filter: "blur(2px)" }}
        />

        {/* Contorno do dente */}
        <path
          d={d}
          fill={status ? getStatusColor(status) : "#ffffff"}
          stroke={isSelected ? "#3b82f6" : isHovered ? "#64748b" : "#cbd5e1"}
          strokeWidth={isSelected ? 3 : isHovered ? 2 : 1.5}
          opacity={status ? 0.9 : 1}
          style={{
            cursor: "pointer",
            filter: isHovered ? "brightness(1.1)" : "none",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={() => setHoveredTooth(toothNumber)}
          onMouseLeave={() => setHoveredTooth(null)}
          onClick={() => {
            setSelectedTooth(toothNumber);
            setSelectedArcada(null);
          }}
        />

        {/* Número do dente */}
        <text
          x="0"
          y="5"
          textAnchor="middle"
          fontSize="12"
          fontWeight="700"
          fill={status ? "#fff" : "#334155"}
          style={{ pointerEvents: "none", userSelect: "none" }}
          transform={isLowerJaw ? "rotate(165)" : ""}
        >
          {toothNumber}
        </text>

        {/* Indicador de múltiplos procedimentos */}
        {procedures.length > 1 && (
          <g>
            <circle
              cx="15"
              cy="-15"
              r="8"
              fill="#3b82f6"
              stroke="#fff"
              strokeWidth="2"
            />
            <text
              x="15"
              y="-11"
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill="#fff"
            >
              {procedures.length}
            </text>
          </g>
        )}
      </g>
    );
  };

  // Renderizar indicadores de arcada
  const ArcadaIndicator = ({ arcada, totalTratamentos }) => {
    const status = getArcadaStatus(arcada);
    if (!status) return null;

    return (
      <g
        style={{ cursor: "pointer" }}
        onClick={() => {
          setSelectedArcada(arcada);
          setSelectedTooth(null);
        }}
      >
        <circle
          cx="0"
          cy="0"
          r="20"
          fill={getStatusColor(status)}
          stroke="#fff"
          strokeWidth="3"
          opacity="0.9"
        />
        <text
          x="0"
          y="11"
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="#fff"
          style={{ pointerEvents: "none" }}
        >
          {totalTratamentos}
        </text>
        <Layers
          size={12}
          x="-6"
          y="-15"
          fill="#fff"
          style={{ pointerEvents: "none" }}
        />
      </g>
    );
  };

  const getProcedureLabel = (proc, index) => {
    if (proc.isIsolado) {
      return "Tratamento Isolado";
    }
    return `Plano de Tratamento #${index + 1}`;
  };

  return (
    <div className={styles.odontogramaContainer}>
      <div className={styles.legend}>
        <div className={`${styles.item} ${styles.executado}`}>
          <div className={styles.circle}></div>
          <span>Tratamento Executado</span>
        </div>
        <div className={`${styles.item} ${styles.emExecucao}`}>
          <div className={styles.circle}></div>
          <span>Em Execução</span>
        </div>
        <div className={`${styles.item} ${styles.porExecutar}`}>
          <div className={styles.circle}></div>
          <span>Por Executar</span>
        </div>
        <div className={`${styles.item}`}>
          <Layers size={16} />
          <span>Tratamento de Arcada</span>
        </div>
      </div>

      <div className={styles.odontogramaBody}>
        <div className={styles.canvasContainer}>
          <svg
            width="720"
            height="500"
            viewBox="0 100 900 500"
            className="mx-auto"
          >
            <defs>
              <radialGradient id="gumGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fda4af" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.1" />
              </radialGradient>

              {/* Gradientes para arcadas com tratamentos */}
              <linearGradient
                id="arcadaSuperiorGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  stopColor={
                    getStatusColor(getArcadaStatus("superior")) || "#fff"
                  }
                  stopOpacity="0.2"
                />
                <stop
                  offset="50%"
                  stopColor={
                    getStatusColor(getArcadaStatus("superior")) || "#fff"
                  }
                  stopOpacity="0.3"
                />
                <stop
                  offset="100%"
                  stopColor={
                    getStatusColor(getArcadaStatus("superior")) || "#fff"
                  }
                  stopOpacity="0.2"
                />
              </linearGradient>

              <linearGradient
                id="arcadaInferiorGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  stopColor={
                    getStatusColor(getArcadaStatus("inferior")) || "#fff"
                  }
                  stopOpacity="0.2"
                />
                <stop
                  offset="50%"
                  stopColor={
                    getStatusColor(getArcadaStatus("inferior")) || "#fff"
                  }
                  stopOpacity="0.3"
                />
                <stop
                  offset="100%"
                  stopColor={
                    getStatusColor(getArcadaStatus("inferior")) || "#fff"
                  }
                  stopOpacity="0.2"
                />
              </linearGradient>
            </defs>

            {/* Arcada Superior - Formato em U pronunciado */}
            <g transform="translate(450, 250)">
              {/* Indicador de tratamento de arcada superior */}
              {getArcadaStatus("superior") && (
                <rect
                  x="-220"
                  y="-160"
                  width="440"
                  height="260"
                  rx="20"
                  fill="url(#arcadaSuperiorGradient)"
                  stroke={getStatusColor(getArcadaStatus("superior"))}
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  opacity="0.5"
                  pointerEvents="none"
                />
              )}

              {/* Badge de tratamentos de arcada superior */}
              {arcadaTratamentos.superior.length +
                arcadaTratamentos.ambas.length >
                0 && (
                <g transform="translate(-240, -140)">
                  <ArcadaIndicator
                    arcada="superior"
                    totalTratamentos={
                      arcadaTratamentos.superior.length +
                      arcadaTratamentos.ambas.length
                    }
                  />
                </g>
              )}

              {/* Contorno da gengiva superior em formato de ferradura */}
              <path
                d="M -200,80 Q -200,20 -180,-20 Q -160,-60 -140,-90 Q -120,-110 -90,-125 Q -60,-135 -30,-140 Q 0,-142 0,-142 Q 30,-140 60,-135 Q 90,-125 120,-110 Q 140,-90 160,-60 Q 180,-20 200,20 Q 200,80 200,80"
                fill="url(#gumGradient)"
                stroke="#f87171"
                strokeWidth="2"
                opacity="0.4"
                pointerEvents="none"
              />

              {/* Molares Superiores Direitos */}
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={18}
                transform="translate(-195, 70) rotate(-40)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={17}
                transform="translate(-190, 15) rotate(-30)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={16}
                transform="translate(-165, -34) rotate(-20)"
              />

              {/* Pré-molares Superiores Direitos */}
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={15}
                transform="translate(-135, -75) rotate(-12) scale(1.2)"
              />
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={14}
                transform="translate(-110, -110) rotate(-6) scale(1.2)"
              />

              {/* Canino Superior Direito */}
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 15,-10 12,0 Q 8,12 0,15 Q -8,12 -12,0 Q -15,-10 -10,-20"
                toothNumber={13}
                transform="translate(-80, -130) rotate(-3) scale(1.2)"
              />

              {/* Incisivos Superiores */}
              <ToothPath
                d="M -8,-18 Q 0,-25 8,-18 Q 12,-8 10,3 Q 5,12 0,15 Q -5,12 -10,3 Q -12,-8 -8,-18"
                toothNumber={12}
                transform="translate(-50, -140) rotate(0) scale(1.2)"
              />
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 14,-8 12,5 Q 6,15 0,18 Q -6,15 -12,5 Q -14,-8 -10,-20"
                toothNumber={11}
                transform="translate(-15, -142) rotate(0) scale(1.2)"
              />
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 14,-8 12,5 Q 6,15 0,18 Q -6,15 -12,5 Q -14,-8 -10,-20"
                toothNumber={21}
                transform="translate(20, -142) rotate(0) scale(1.2)"
              />
              <ToothPath
                d="M -8,-18 Q 0,-25 8,-18 Q 12,-8 10,3 Q 5,12 0,15 Q -5,12 -10,3 Q -12,-8 -8,-18"
                toothNumber={22}
                transform="translate(57, -140) rotate(0) scale(1.2)"
              />

              {/* Canino Superior Esquerdo */}
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 15,-10 12,0 Q 8,12 0,15 Q -8,12 -12,0 Q -15,-10 -10,-20"
                toothNumber={23}
                transform="translate(92, -118) rotate(3) scale(1.2)"
              />

              {/* Pré-molares Superiores Esquerdos */}
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={24}
                transform="translate(125, -95) rotate(20) scale(1.2)"
              />
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={25}
                transform="translate(150, -62) rotate(32) scale(1.2)"
              />

              {/* Molares Superiores Esquerdos */}
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={26}
                transform="translate(170, -20) rotate(20)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={27}
                transform="translate(195, 20) rotate(30)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={28}
                transform="translate(200, 70) rotate(40)"
              />
            </g>

            {/* Arcada Inferior - Formato em U mais estreito */}
            <g transform="translate(450, 450)">
              {/* Indicador de tratamento de arcada inferior */}
              {getArcadaStatus("inferior") && (
                <rect
                  x="-220"
                  y="-100"
                  width="440"
                  height="260"
                  rx="20"
                  fill="url(#arcadaInferiorGradient)"
                  stroke={getStatusColor(getArcadaStatus("inferior"))}
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  opacity="0.5"
                  pointerEvents="none"
                />
              )}

              {/* Badge de tratamentos de arcada inferior */}
              {arcadaTratamentos.inferior.length +
                arcadaTratamentos.ambas.length >
                0 && (
                <g transform="translate(-240, 140)">
                  <ArcadaIndicator
                    arcada="inferior"
                    totalTratamentos={
                      arcadaTratamentos.inferior.length +
                      arcadaTratamentos.ambas.length
                    }
                  />
                </g>
              )}

              {/* Contorno da gengiva inferior em formato de ferradura */}
              <path
                d="M -200,-80 Q -200,-20 -180,20 Q -160,60 -140,90 Q -120,110 -90,125 Q -60,135 -30,140 Q 0,142 0,142 Q 30,140 60,135 Q 90,125 120,110 Q 140,90 160,60 Q 180,20 200,-20 Q 200,-80 200,-80"
                fill="url(#gumGradient)"
                stroke="#f87171"
                strokeWidth="2"
                opacity="0.4"
                pointerEvents="none"
              />

              {/* Molares Inferiores Direitos */}
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={48}
                transform="translate(-195, -70) rotate(140)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={47}
                transform="translate(-190, -15) rotate(150)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={46}
                transform="translate(-165, 34) rotate(160)"
              />

              {/* Pré-molares Inferiores Direitos */}
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={45}
                transform="translate(-135, 75) rotate(200) scale(1.2)"
              />
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={44}
                transform="translate(-110, 110) rotate(200) scale(1.2)"
              />

              {/* Canino Inferior Direito */}
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 15,-10 12,0 Q 8,12 0,15 Q -8,12 -12,0 Q -15,-10 -10,-20"
                toothNumber={43}
                transform="translate(-80, 130) rotate(200) scale(1.2)"
              />

              {/* Incisivos Inferiores */}
              <ToothPath
                d="M -6,-15 Q 0,-20 6,-15 Q 10,-6 8,2 Q 4,10 0,12 Q -4,10 -8,2 Q -10,-6 -6,-15"
                toothNumber={42}
                transform="translate(-50, 140) rotate(180) scale(1.3)"
              />
              <ToothPath
                d="M -7,-16 Q 0,-22 7,-16 Q 11,-7 9,3 Q 5,12 0,14 Q -5,12 -9,3 Q -11,-7 -7,-16"
                toothNumber={41}
                transform="translate(-15, 142) rotate(180) scale(1.3)"
              />
              <ToothPath
                d="M -7,-16 Q 0,-22 7,-16 Q 11,-7 9,3 Q 5,12 0,14 Q -5,12 -9,3 Q -11,-7 -7,-16"
                toothNumber={31}
                transform="translate(20, 142) rotate(180) scale(1.3)"
              />
              <ToothPath
                d="M -6,-15 Q 0,-20 6,-15 Q 10,-6 8,2 Q 4,10 0,12 Q -4,10 -8,2 Q -10,-6 -6,-15"
                toothNumber={32}
                transform="translate(50, 140) rotate(180) scale(1.3)"
              />

              {/* Canino Inferior Esquerdo */}
              <ToothPath
                d="M -10,-20 Q 0,-28 10,-20 Q 15,-10 12,0 Q 8,12 0,15 Q -8,12 -12,0 Q -15,-10 -10,-20"
                toothNumber={33}
                transform="translate(82, 118) rotate(183) scale(1.2)"
              />

              {/* Pré-molares Inferiores Esquerdos */}
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={34}
                transform="translate(115, 95) rotate(186) scale(1.1)"
              />
              <ToothPath
                d="M -12,-22 Q -5,-30 5,-30 Q 12,-22 15,-12 Q 15,0 8,10 Q 0,15 -8,10 Q -15,0 -15,-12 Q -12,-22 -12,-22"
                toothNumber={35}
                transform="translate(140, 67) rotate(170) scale(1.2)"
              />

              {/* Molares Inferiores Esquerdos */}
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={36}
                transform="translate(175, 30) rotate(200) scale(0.9)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={37}
                transform="translate(195, -20) rotate(210)"
              />
              <ToothPath
                d="M -15,-28 Q -5,-38 5,-38 Q 15,-38 25,-28 Q 28,-15 25,-5 Q 20,8 10,15 Q 0,20 -10,15 Q -20,8 -25,-5 Q -28,-15 -15,-28"
                toothNumber={38}
                transform="translate(200, -70) rotate(220)"
              />
            </g>

            {/* Arcada de Criança ao centro do Odontograma Adulto */}
            <g transform="translate(450, 250) scale(0.45)">
              {/* Gengiva superior infantil */}
              <path
                d="M -200,80 Q -200,20 -180,-20 Q -160,-60 -140,-90 Q -120,-110 -90,-125 Q -60,-135 -30,-140 Q 0,-142 0,-142 Q 30,-140 60,-135 Q 90,-125 120,-110 Q 140,-90 160,-60 Q 180,-20 200,20 Q 200,80 200,80"
                fill="url(#gumGradient)"
                stroke="#f87171"
                strokeWidth="2"
                opacity="0.3"
                pointerEvents="none"
              />
              <ToothPath
                d="M -10,-18 Q 0,-26 10,-18 Q 14,-8 12,4 Q 5,14 0,17 Q -5,14 -12,4 Q -14,-8 -10,-18"
                toothNumber={55}
                transform="translate(-200, 55) rotate(-35) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={54}
                transform="translate(-175, -15) rotate(-25) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={53}
                transform="translate(-145, -85) rotate(-15) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={52}
                transform="translate(-90, -116) rotate(-5) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={51}
                transform="translate(-27, -130) rotate(0) scale(2.3)"
              />

              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={61}
                transform="translate(27, -130) rotate(0) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={62}
                transform="translate(85, -116) rotate(5) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={63}
                transform="translate(135, -85) rotate(15) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={64}
                transform="translate(170, -20) rotate(25) scale(2.3)"
              />
              <ToothPath
                d="M -10,-18 Q 0,-26 10,-18 Q 14,-8 12,4 Q 5,14 0,17 Q -5,14 -12,4 Q -14,-8 -10,-18"
                toothNumber={65}
                transform="translate(190, 55) rotate(35) scale(2.3)"
              />
            </g>

            <g transform="translate(450, 450) scale(0.45)">
              {/* Gengiva inferior infantil */}
              <path
                d="M -200,-80 Q -200,-20 -180,20 Q -160,60 -140,90 Q -120,110 -90,125 Q -60,135 -30,140 Q 0,142 0,142 Q 30,140 60,135 Q 90,125 120,110 Q 140,90 160,60 Q 180,20 200,-20 Q 200,-80 200,-80"
                fill="url(#gumGradient)"
                stroke="#f87171"
                strokeWidth="2"
                opacity="0.3"
                pointerEvents="none"
              />
              <ToothPath
                d="M -10,-18 Q 0,-26 10,-18 Q 14,-8 12,4 Q 5,14 0,17 Q -5,14 -12,4 Q -14,-8 -10,-18"
                toothNumber={85}
                transform="translate(-200, -55) rotate(-145) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={84}
                transform="translate(-175, 15) rotate(-145) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={83}
                transform="translate(-145, 85) rotate(-160) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={82}
                transform="translate(-90, 116) rotate(205) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={81}
                transform="translate(-27, 130) rotate(180) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={71}
                transform="translate(27, 130) rotate(180) scale(2.3)"
              />
              <ToothPath
                d="M -8,-16 Q 0,-22 8,-16 Q 12,-6 10,4 Q 5,12 0,14 Q -5,12 -10,4 Q -12,-6 -8,-16"
                toothNumber={72}
                transform="translate(85, 116) rotate(165) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={73}
                transform="translate(135, 85) rotate(155) scale(2.3)"
              />
              <ToothPath
                d="M -9,-17 Q 0,-24 9,-17 Q 13,-7 11,4 Q 5,13 0,15 Q -5,13 -11,4 Q -13,-7 -9,-17"
                toothNumber={74}
                transform="translate(170, 20) rotate(145) scale(2.3)"
              />
              <ToothPath
                d="M -10,-18 Q 0,-26 10,-18 Q 14,-8 12,4 Q 5,14 0,17 Q -5,14 -12,4 Q -14,-8 -10,-18"
                toothNumber={75}
                transform="translate(190, -55) rotate(145) scale(2.3)"
              />
            </g>
          </svg>
        </div>

        {/* Painel de detalhes */}
        {(selectedTooth || selectedArcada) && (
          <div className={styles.detailPanel}>
            <div className={styles.header}>
              <h3>
                {selectedArcada ? (
                  <>
                    <Layers size={24} className="text-blue-600" />
                    Arcada{" "}
                    {selectedArcada === "superior"
                      ? "Superior"
                      : selectedArcada === "inferior"
                      ? "Inferior"
                      : "Completa"}
                  </>
                ) : (
                  <>
                    <FileText size={24} className="text-blue-600" />
                    Dente {selectedTooth}
                  </>
                )}
              </h3>
              <button
                onClick={() => {
                  setSelectedTooth(null);
                  setSelectedArcada(null);
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Mostrar tratamentos de arcada quando arcada selecionada */}
              {selectedArcada && (
                <>
                  {(() => {
                    const tratamentos = [
                      ...(selectedArcada === "superior"
                        ? [
                            ...arcadaTratamentos.superior,
                            ...arcadaTratamentos.ambas,
                          ]
                        : []),
                      ...(selectedArcada === "inferior"
                        ? [
                            ...arcadaTratamentos.inferior,
                            ...arcadaTratamentos.ambas,
                          ]
                        : []),
                    ];

                    if (tratamentos.length > 0) {
                      return (
                        <>
                          <div className={styles.summary}>
                            <Info size={16} />
                            <p>
                              {tratamentos.length} tratamento(s) de arcada
                              registado(s)
                            </p>
                          </div>

                          {tratamentos.map((proc, index) => (
                            <div key={index} className={styles.procedureCard}>
                              <div className={styles.cardHeader}>
                                <div>
                                  <h4>{proc.tratamento.designacao}</h4>
                                  <p className={styles.arcadaType}>
                                    {proc.isIsolado ? (
                                      <span className="flex items-center gap-1">
                                        <User size={14} />
                                        Tratamento Isolado -{" "}
                                        {proc.dente || "Arcada completa"}
                                      </span>
                                    ) : (
                                      proc.dente || "Arcada completa"
                                    )}
                                  </p>
                                </div>
                                <span
                                  className={`${styles.statusTag} ${
                                    styles[
                                      proc.procedimento_completo
                                        ? "executado"
                                        : !proc.isIsolado &&
                                          proc.data_inicio_execucao
                                        ? "em_execucao"
                                        : "por_executar"
                                    ]
                                  }`}
                                >
                                  {proc.procedimento_completo
                                    ? "Executado"
                                    : !proc.isIsolado &&
                                      proc.data_inicio_execucao
                                    ? "Em Execução"
                                    : "Por Executar"}
                                </span>
                              </div>

                              <div className={styles.cardBody}>
                                <div className={styles.statusLine}>
                                  {proc.procedimento_completo && (
                                    <CheckCircle size={16} className="icon" />
                                  )}
                                  {!proc.procedimento_completo &&
                                    proc.data_inicio_execucao && (
                                      <Clock size={16} className="icon" />
                                    )}
                                  {!proc.data_inicio_execucao && (
                                    <AlertCircle size={16} className="icon" />
                                  )}
                                  <span>
                                    {proc.procedimento_completo
                                      ? "Concluído"
                                      : proc.data_inicio_execucao
                                      ? "Em andamento"
                                      : "Aguardando início"}
                                  </span>
                                </div>

                                {proc.data_inicio_execucao && (
                                  <div className={styles.dateLine}>
                                    <span>
                                      <strong>Início:</strong>{" "}
                                      {formatDate(proc.data_inicio_execucao)}
                                    </span>
                                  </div>
                                )}

                                {proc.data_fim_execucao && (
                                  <div className={styles.dateLine}>
                                    <span>
                                      <strong>Conclusão:</strong>{" "}
                                      {formatDate(proc.data_fim_execucao)}
                                    </span>
                                  </div>
                                )}

                                {!proc.data_inicio_execucao && (
                                  <div className={styles.notStarted}>
                                    Tratamento ainda não iniciado
                                  </div>
                                )}

                                {proc.medico && (
                                  <div className={styles.dateLine}>
                                    <span>
                                      <strong>Médico:</strong>{" "}
                                      {proc.medico.name || "N/A"}
                                    </span>
                                  </div>
                                )}

                                {proc.observacoes && (
                                  <div className={styles.dateLine}>
                                    <span>
                                      <strong>Observações:</strong>{" "}
                                      {proc.observacoes}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      );
                    } else {
                      return (
                        <div className={styles.emptyState}>
                          <Layers size={64} className="mb-4" />
                          <p>Nenhum tratamento de arcada</p>
                          <p className={styles.subtext}>
                            Esta arcada não possui tratamentos registados
                          </p>
                        </div>
                      );
                    }
                  })()}
                </>
              )}

              {/* Mostrar tratamentos individuais quando dente selecionado */}
              {selectedTooth && (
                <>
                  {getToothProcedures(selectedTooth).length > 0 ? (
                    <>
                      <div className={styles.summary}>
                        <Info size={16} />
                        <p>
                          {getToothProcedures(selectedTooth).length}{" "}
                          procedimento(s) registado(s)
                        </p>
                      </div>

                      {getToothProcedures(selectedTooth).map((proc, index) => (
                        <div key={index} className={styles.procedureCard}>
                          <div className={styles.cardHeader}>
                            <div>
                              <h4>{proc.tratamento.designacao}</h4>
                              <p className="flex items-center gap-1">
                                {proc.isIsolado ? (
                                  // ADICIONADO
                                  <>
                                    <User size={14} />
                                    Tratamento Isolado
                                  </>
                                ) : (
                                  <>
                                    <FileText size={14} />
                                    Plano de Tratamento #{index + 1}
                                  </>
                                )}
                              </p>
                            </div>
                            <span
                              className={`${styles.statusTag} ${
                                styles[proc.status]
                              }`}
                            >
                              {proc.status === "executado"
                                ? "Executado"
                                : proc.status === "em_execucao"
                                ? "Em Execução"
                                : "Por Executar"}
                            </span>
                          </div>

                          <div className={styles.cardBody}>
                            <div className={styles.statusLine}>
                              {proc.status === "executado" && (
                                <CheckCircle size={16} className="icon" />
                              )}
                              {proc.status === "em_execucao" && (
                                <Clock size={16} className="icon" />
                              )}
                              {proc.status === "por_executar" && (
                                <AlertCircle size={16} className="icon" />
                              )}
                              <span>
                                {proc.status === "executado"
                                  ? "Concluído"
                                  : proc.status === "em_execucao"
                                  ? "Em andamento"
                                  : "Aguardando início"}
                              </span>
                            </div>
                            {proc.data_inicio_execucao && (
                              <div className={styles.dateLine}>
                                <span>
                                  <strong>Início:</strong>{" "}
                                  {formatDate(proc.data_inicio_execucao)}
                                </span>
                              </div>
                            )}
                            {proc.data_fim_execucao && (
                              <div className={styles.dateLine}>
                                <span>
                                  <strong>Conclusão:</strong>{" "}
                                  {formatDate(proc.data_fim_execucao)}
                                </span>
                              </div>
                            )}
                            {!proc.data_inicio_execucao && (
                              <div className={styles.notStarted}>
                                Tratamento ainda não iniciado
                              </div>
                            )}

                            {proc.medico && (
                              <div className={styles.dateLine}>
                                <span>
                                  <strong>Médico:</strong>{" "}
                                  {proc.medico.name || "N/A"}
                                </span>
                              </div>
                            )}
                            {proc.observacoes && (
                              <div className={styles.dateLine}>
                                <span>
                                  <strong>Observações:</strong>{" "}
                                  {proc.observacoes}
                                </span>
                              </div>
                            )}
                            {proc.relatorio && (
                              <div className={styles.dateLine}>
                                <span>
                                  <strong>Relatório:</strong> {proc.relatorio}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className={styles.emptyState}>
                      <Calendar size={64} className="mb-4" />
                      <p>Nenhum procedimento agendado</p>
                      <p className={styles.subtext}>
                        Este dente não possui tratamentos registados
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Odontograma;
